/**
 * Who may aim a probe (port check, credential test) at which address.
 *
 * Private networks overlap between customers: two customers can both have a
 * camera at 172.16.254.101. A customer typing their own LAN address could
 * reach another customer's device, and a credential test there could lock
 * that device's account. So customers pick private targets from their own
 * addresses; only Actuate staff type any address. Public addresses cannot
 * overlap, and "is my port forward open?" needs one typed in before the
 * camera is onboarded, so everyone may type those.
 *
 * admin enforces the same rule (403 `target_not_yours`); this mirrors it so
 * the form says why before anything is sent.
 */
import { cidrContains } from "./siteTunnel";
import { EndpointSite, endpointProfile } from "./integrationEndpoints";

/** One address from `GET network-targets/`: a camera, a site server or a tunnel subnet. */
export interface NetworkTarget {
  /** An IP, a hostname, or for `tunnel_subnet` a CIDR. */
  address: string;
  ports: number[];
  label: string;
  siteId: number | null;
  siteName: string | null;
  cameraId: number | null;
  source:
    | "camera"
    | "server"
    | "management_server"
    | "event_server"
    | "tunnel_subnet"
    /** A tunnel's own client address: the device running WireGuard (e.g. an NVR). */
    | "tunnel_client"
    /** A Milestone recording server. */
    | "recording_server"
    /** A vendor relay the connector goes through (e.g. Nx cloud). */
    | "relay"
    /** One of the user's own IPsec site-to-site ranges (a CIDR; ENG-2055). */
    | "ipsec_range"
    /** An Immix/Dice receiver the site's alarms go to (ENG-2063). */
    | "alarm_receiver";
  tunnelId: number | null;
  /**
   * admin's `default_path` (ENG-2055): whether the Default path reaches this
   * address for this user (true for their own IPsec ranges, false for
   * WireGuard-only addresses). Absent when unknown (e.g. built locally).
   */
  defaultPath?: boolean | null;
}

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

const PRIVATE_V4 = ["10.0.0.0/8", "172.16.0.0/12", "192.168.0.0/16"];

/** An IPv4 or IPv6 literal (not a hostname). */
export const isIpLiteral = (host: string): boolean => {
  const text = host.trim().replace(/^\[|\]$/g, "");
  const m = IPV4.exec(text);
  if (m) return m.slice(1).every((octet) => Number(octet) <= 255);
  return text.includes(":") && /^[0-9a-f:.]+$/i.test(text);
};

/** RFC 1918 IPv4, or IPv6 unique-local (fc00::/7). */
export const isPrivateAddress = (host: string): boolean => {
  const text = host
    .trim()
    .replace(/^\[|\]$/g, "")
    .toLowerCase();
  if (IPV4.test(text))
    return PRIVATE_V4.some((cidr) => cidrContains(cidr, text));
  return /^f[cd][0-9a-f]{2}:/.test(text);
};

/**
 * Option C (ENG-2055, admin #3020 enforces it): a customer may check a
 * public address or hostname from any origin. A private address from the
 * Default path is allowed when the NAT route for it leads into one of their
 * own IPsec VPNs; admin decides that and answers 403 target_not_yours
 * otherwise. The browser only refuses what it already knows the answer to:
 * an address its known rows say the Default path doesn't reach (WireGuard
 * networks), and, through a picked tunnel, an address outside its ranges.
 */
export type TargetOrigin =
  | { kind: "nat" }
  | { kind: "wireguard"; subnets: string[] };

export type TargetVerdict =
  | { allowed: true }
  | { allowed: false; reason: "private_from_default" | "outside_tunnel" };

/** A known row matches a host: the same address, or a range containing it. */
const rowCovers = (row: NetworkTarget, host: string): boolean =>
  row.source === "tunnel_subnet" || row.source === "ipsec_range"
    ? cidrContains(row.address, host)
    : row.address.trim() === host;

export const customerTargetVerdict = (
  host: string,
  origin: TargetOrigin,
  known: NetworkTarget[] = []
): TargetVerdict => {
  const text = host.trim();
  if (!text || !isIpLiteral(text) || !isPrivateAddress(text))
    return { allowed: true };
  if (origin.kind === "nat") {
    // Refuse only what a known row says the Default path can't reach;
    // anything else goes to admin, which has the route table.
    const matches = known.filter(
      (row) => typeof row.defaultPath === "boolean" && rowCovers(row, text)
    );
    return matches.length > 0 && matches.every((row) => !row.defaultPath)
      ? { allowed: false, reason: "private_from_default" }
      : { allowed: true };
  }
  return origin.subnets.some((cidr) => cidrContains(cidr, text))
    ? { allowed: true }
    : { allowed: false, reason: "outside_tunnel" };
};

/** A known WireGuard-only address (the Default path doesn't reach it). */
export const PRIVATE_FROM_DEFAULT_MESSAGE =
  "The Default path doesn't reach this address: it's on a WireGuard network. Choose its WireGuard tunnel under Test from.";

export const OUTSIDE_TUNNEL_MESSAGE =
  "That address isn't in this tunnel's networks. Choose the tunnel that routes it under Test from.";

/** A camera's recording server, as admin's camera/site/ sends it (ENG-2063). */
export interface RecordingServerRef {
  id: number;
  name: string;
  server_ip: string;
}

const RECEIVER_KIND_LABEL: Record<string, string> = {
  immix: "Immix",
  dice: "Dice",
};

/** Milestone's image server port when a site doesn't say otherwise. */
const DEFAULT_RECORDING_SERVER_PORTS = [7563];

/**
 * `network-targets/` recording_server rows carry no port (admin stores
 * none): give them the site profile's image-server ports when the row is
 * the picked site's, else Milestone's default 7563.
 */
export const withRecordingServerPorts = (
  rows: NetworkTarget[],
  site: (EndpointSite & { id: number }) | null
): NetworkTarget[] =>
  rows.map((row) => {
    if (row.source !== "recording_server" || row.ports.length) return row;
    const profilePorts =
      site && site.id === row.siteId
        ? endpointProfile(site).recordingServerPorts
        : [];
    return {
      ...row,
      ports: profilePorts.length
        ? profilePorts
        : DEFAULT_RECORDING_SERVER_PORTS,
    };
  });

/**
 * The picked site's own addresses for the host picker (ENG-2058), built
 * from data the page already has, so they show on prod before admin's
 * `network-targets/` is deployed: each camera's saved address, the site's
 * servers, and for each tunnel serving the site its subnets and, when
 * WireGuard runs on the device itself, its client address.
 */
const uniquePorts = (values: (number | null | undefined)[]): number[] =>
  values.filter(
    (port, index, all): port is number =>
      typeof port === "number" &&
      port > 0 &&
      port < 65536 &&
      all.indexOf(port) === index
  );

export const siteLocalTargets = ({
  site,
  cameras,
  tunnels,
  serverPorts,
  recordingServers = [],
  alarmReceivers = [],
}: {
  site: {
    id: number;
    name: string;
    server_ip: string | null;
    management_server_ip: string | null;
    event_server_ip: string | null;
    alert_port?: number | null;
    milestone_http_port?: number | null;
    milestone_ssl_port?: number | null;
    integration_type_name?: string | null;
    server_port?: number | null;
    stream_port?: number | null;
    cloud_system_id?: string | null;
  } | null;
  cameras: {
    id: number;
    camera_name: string;
    ip?: string | null;
    rtsp?: number | null;
    http?: number | null;
    /** admin's camera/site/ `recording_server` (ENG-2063); absent before it. */
    recording_server?: RecordingServerRef | null;
  }[];
  tunnels: {
    id: number;
    name: string;
    subnets: string[] | null;
    client_tunnel_ip: string;
  }[];
  serverPorts: number[];
  /** Milestone recording servers (default ports 7563 image server, 80). */
  recordingServers?: { name: string; server_ip: string }[];
  /** Alarm receivers the user can see; the site's Immix/Dice ones are listed. */
  alarmReceivers?: {
    type: string;
    host: string;
    port: number | null;
    site_ids: number[];
  }[];
}): NetworkTarget[] => {
  if (!site) return [];
  const base = { siteId: site.id, siteName: site.name };
  const rows: NetworkTarget[] = [];
  const cameraPorts = endpointProfile(site).cameraPorts;
  cameras.forEach((camera) => {
    const address = (camera.ip || "").trim();
    if (address)
      rows.push({
        ...base,
        address,
        // The camera's own ports first (e.g. RTSP over HTTP on 6007), then the
        // integration's defaults.
        ports: uniquePorts([camera.rtsp, camera.http, ...cameraPorts]),
        // A Milestone camera streams from its recording server: name it.
        label: camera.recording_server?.name
          ? `${camera.camera_name} · on ${camera.recording_server.name}`
          : camera.camera_name,
        cameraId: camera.id,
        source: "camera",
        tunnelId: null,
      });
  });
  const server = (
    kind: NetworkTarget["source"],
    address: string | null,
    ports: number[],
    label: string
  ) => {
    const text = (address || "").trim();
    if (text)
      rows.push({
        ...base,
        address: text,
        ports,
        label,
        cameraId: null,
        source: kind,
        tunnelId: null,
      });
  };
  // What the connector dials for this integration, with the default ports
  // it falls back to when a field is blank (integrationEndpoints).
  const profile = endpointProfile(site);
  profile.endpoints.forEach((endpoint) =>
    server(
      endpoint.role === "relay" ? "relay" : endpoint.role,
      endpoint.address,
      endpoint.ports,
      endpoint.label
    )
  );
  // The site's recording servers, plus any its cameras name (ENG-2063).
  [
    ...recordingServers,
    ...cameras.flatMap((camera) =>
      camera.recording_server ? [camera.recording_server] : []
    ),
  ].forEach((recorder) =>
    server(
      "recording_server",
      recorder.server_ip,
      profile.recordingServerPorts.length
        ? profile.recordingServerPorts
        : [7563, 80],
      recorder.name
    )
  );
  // Where the site's alarms are delivered. Receivers are public by nature;
  // admin decides who may probe them.
  alarmReceivers
    .filter(
      (receiver) =>
        (receiver.type === "immix" || receiver.type === "dice") &&
        receiver.site_ids.includes(site.id)
    )
    .forEach((receiver) =>
      server(
        "alarm_receiver",
        receiver.host,
        receiver.port ? [receiver.port] : [],
        `Alarm receiver (${RECEIVER_KIND_LABEL[receiver.type]})`
      )
    );
  tunnels.forEach((tunnel) => {
    const subnets = (tunnel.subnets || []).filter((cidr) => cidr.trim());
    subnets.forEach((cidr) =>
      rows.push({
        address: cidr,
        ports: [],
        label: tunnel.name,
        siteId: null,
        siteName: null,
        cameraId: null,
        source: "tunnel_subnet",
        defaultPath: false,
        tunnelId: tunnel.id,
      })
    );
    const own = (tunnel.client_tunnel_ip || "").trim().replace(/\/32$/, "");
    if (!subnets.length && own)
      rows.push({
        address: own,
        ports: [],
        label: `WireGuard device (${tunnel.name})`,
        siteId: null,
        siteName: null,
        cameraId: null,
        source: "tunnel_client",
        defaultPath: false,
        tunnelId: tunnel.id,
      });
  });
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = `${row.source}|${row.address}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * How many of the site's cameras stream from each recording server, by
 * address, from camera/site/'s `recording_server` (ENG-2063). Empty
 * before admin sends it, so the card lists the servers as before.
 */
export const camerasPerRecordingServer = (
  cameras: { recording_server?: { server_ip: string } | null }[]
): Map<string, number> => {
  const counts = new Map<string, number>();
  cameras.forEach((camera) => {
    const address = camera.recording_server?.server_ip?.trim();
    if (address) counts.set(address, (counts.get(address) ?? 0) + 1);
  });
  return counts;
};

/** Local targets matching what the user typed (address prefix or label). */
export const filterTargets = (
  targets: NetworkTarget[],
  query: string
): NetworkTarget[] => {
  const q = query.trim().toLowerCase();
  if (!q) return targets;
  // A host that is already one of the site's addresses (filled in by a
  // site pick or Check reachability) keeps the whole list visible, so the
  // other servers are one click away; typing something new narrows it.
  if (targets.some((target) => target.address.toLowerCase() === q))
    return targets;
  return targets.filter(
    (target) =>
      target.address.toLowerCase().startsWith(q) ||
      target.label.toLowerCase().includes(q) ||
      (target.siteName || "").toLowerCase().includes(q)
  );
};
